import React, { useState } from 'react';

import {
  Badge,
  Button,
  Caption,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  LoadingIcon,
  Skeleton,
  Stack,
  Text,
} from '@eluve/components';
import { useAppointmentContext } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import {
  Citation,
  DifferentialDiagnosisOutputSchema,
  differentialDiagnosesSchema,
} from '@eluve/llm-outputs';

import { CitationChip } from '../citations/CitationChip';
import {
  BaseCitation,
  CitationDetailsModal,
  getDocumentTypeInfo,
} from '../citations/CitationComponents';

import { OutputTitle } from './OutputTitle';

// Adapter function to convert Citation to BaseCitation
const citationToBaseCitation = (citation: Citation): BaseCitation => ({
  documentTitle: citation.document_title || 'Source',
  citedText: citation.cited_text,
  artifactId: citation.artifact_id,
});

// Component to render top-level citations for a diagnosis
const DiagnosisCitations: React.FC<{ citations: Citation[] }> = ({
  citations,
}) => {
  const { tenantId } = useAppointmentContext();
  const [clickedCitation, setClickedCitation] = useState<BaseCitation | null>(
    null,
  );

  const seenSources = new Set<string>();
  const uniqueCitations = citations.filter((citation) => {
    const locationKey =
      citation.type === 'char_location'
        ? `${citation.start_char_index}-${citation.end_char_index}`
        : citation.type === 'page_location'
          ? `${citation.start_page_number}-${citation.end_page_number}`
          : citation.cited_text;
    const sourceKey = `${citation.document_title}-${citation.document_index}-${locationKey}`;
    if (seenSources.has(sourceKey)) {
      return false;
    }
    seenSources.add(sourceKey);
    return true;
  });

  return (
    <>
      <Stack gap={2} direction="horizontal" wrap>
        {uniqueCitations.map((citation, idx) => {
          const baseCitation = citationToBaseCitation(citation);
          const { label, icon } = getDocumentTypeInfo(
            baseCitation.documentTitle,
          );

          return (
            <CitationChip
              key={idx}
              label={label}
              icon={icon}
              onClick={() => setClickedCitation(baseCitation)}
            />
          );
        })}
      </Stack>

      {clickedCitation && tenantId && (
        <CitationDetailsModal
          citation={clickedCitation}
          isOpen={true}
          onClose={() => setClickedCitation(null)}
          tenantId={tenantId}
        />
      )}
    </>
  );
};

export interface DifferentialDiagnosesCardProps {
  candidateDiagnosis: string;
  supportingEvidence: string[];
  clinicalUrgency: 'strong' | 'moderate' | 'weak';
  currentDifferentialGap: string;
  suggestedNextSteps: string;
  keyUncertainty: string;
  citations?: Citation[];
  onFeedback?: (isHelpful: boolean, diagnosis: string) => void;
  onCardToggle?: (
    isOpen: boolean,
    diagnosis: string,
    signalStrength: string,
  ) => void;
  isOpenByDefault?: boolean;
}

const mapDiagnosisToCardProps = (
  diagnosis: DifferentialDiagnosisOutputSchema,
): Omit<
  DifferentialDiagnosesCardProps,
  'onFeedback' | 'onCardToggle' | 'isOpenByDefault'
> => ({
  candidateDiagnosis: diagnosis.candidate_diagnosis,
  supportingEvidence: diagnosis.supporting_evidence,
  clinicalUrgency: diagnosis.clinical_urgency,
  currentDifferentialGap: diagnosis.current_differential_gap,
  suggestedNextSteps: diagnosis.suggested_next_steps,
  keyUncertainty: diagnosis.key_uncertainty,
  citations: diagnosis.citations,
});

export const DifferentialDiagnosisCard: React.FC<
  DifferentialDiagnosesCardProps
> = ({
  candidateDiagnosis,
  supportingEvidence,
  clinicalUrgency,
  currentDifferentialGap,
  suggestedNextSteps,
  keyUncertainty,
  citations,
  onFeedback,
  onCardToggle,
  isOpenByDefault = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const [feedbackGiven, setFeedbackGiven] = useState<boolean>(false);
  const [isHelpful, setIsHelpful] = useState<boolean>(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onCardToggle?.(open, candidateDiagnosis, clinicalUrgency);
  };

  const feedbackHandler = (helpful: boolean) => {
    setFeedbackGiven(true);
    setIsHelpful(helpful);
    onFeedback?.(helpful, candidateDiagnosis);
  };

  const hasCitations = citations && citations.length > 0;
  const hasEvidence = supportingEvidence.length > 0;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      key={candidateDiagnosis}
      className="w-full rounded-xl bg-bg-secondary px-4 py-2"
    >
      <Stack
        justify="between"
        className={`hover:cursor-pointer ${isOpen ? 'pb-1' : ''}`}
        onClick={() => handleOpenChange(!isOpen)}
        gap={2}
        direction="horizontal"
      >
        <Stack
          gap={2}
          className="min-w-0 flex-1"
          direction="horizontal"
          align="center"
        >
          <Text inline color="secondary" className="truncate">
            {candidateDiagnosis}
          </Text>

          {clinicalUrgency === 'strong' && (
            <Badge variant="destructive" className="shrink-0">
              {t('appointmentTools.outputs.urgencyHigh')}
            </Badge>
          )}
        </Stack>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="lg">
            <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size="auto" />
          </Button>
        </CollapsibleTrigger>
      </Stack>

      <CollapsibleContent>
        <Stack gap={5}>
          <div className="h-px w-full bg-border-primary" />

          {hasEvidence && (
            <Stack gap={2}>
              <Caption size="2xs" weight="bold" color="supporting">
                {t('appointmentTools.outputs.supportingEvidence')}
              </Caption>
              {supportingEvidence.map((evidence) => (
                <Stack
                  key={evidence}
                  gap={2}
                  direction="horizontal"
                  align="start"
                >
                  <div className="mt-1">
                    <Icon name="Check" className="text-text-positive" />
                  </div>
                  <Text inline color="secondary">
                    {evidence}
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}

          <Stack gap={2}>
            <Caption size="2xs" weight="bold" color="supporting">
              {t('appointmentTools.outputs.differentialGap')}
            </Caption>
            <Text color="secondary">{currentDifferentialGap}</Text>
          </Stack>

          <Stack gap={2}>
            <Caption size="2xs" weight="bold" color="supporting">
              {t('appointmentTools.outputs.keyUncertainty')}
            </Caption>
            <Text color="secondary">{keyUncertainty}</Text>
          </Stack>

          <Stack gap={2}>
            <Caption size="2xs" weight="bold" color="supporting">
              {t('appointmentTools.outputs.suggestedNextSteps')}
            </Caption>
            <Text color="secondary">{suggestedNextSteps}</Text>
          </Stack>

          {hasCitations && (
            <Stack gap={2}>
              <Caption size="2xs" weight="bold" color="supporting">
                {t('appointmentTools.outputs.sourcesCaption')}
              </Caption>
              <DiagnosisCitations citations={citations} />
            </Stack>
          )}

          <div className="h-px w-full bg-border-primary" />

          <Stack
            className="mb-2"
            justify="between"
            gap={2}
            direction="horizontal"
          >
            <Text inline size="xs" color="tertiary">
              {t('appointmentTools.outputs.insightHelpful')}
            </Text>
            <Stack className="w-fit" gap={2} direction="horizontal">
              <Button
                variant={
                  feedbackGiven && isHelpful === false ? 'ghost' : 'outline'
                }
                size="sm"
                disabled={feedbackGiven}
                onClick={() => feedbackHandler(true)}
              >
                {t('appointment.feedback.yes')}
              </Button>
              <Button
                variant={
                  feedbackGiven && isHelpful === true ? 'ghost' : 'outline'
                }
                size="sm"
                disabled={feedbackGiven}
                onClick={() => feedbackHandler(false)}
              >
                {t('appointment.feedback.no')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const DifferentialDiagnosisCardLoading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Collapsible
      open={false}
      key="placeholder"
      disabled={true}
      className="w-full rounded-xl bg-bg-secondary px-4 py-2"
    >
      <Stack justify="between" gap={2} direction="horizontal">
        <Text inline color="secondary" className="animate-pulse">
          {t('appointmentTools.outputs.analyzingDiagnosis')}
          <Skeleton className="w-1/2" />
        </Text>
        <CollapsibleTrigger asChild>
          <LoadingIcon size="lg" className="mx-3 h-[44px] py-2" />
        </CollapsibleTrigger>
      </Stack>
    </Collapsible>
  );
};

export interface DifferentialDiagnosesOutputProps {
  userAddedDiagnoses?: DifferentialDiagnosisOutputSchema[];
  content: Record<string, unknown> | Record<string, unknown>[];
  showTitle?: boolean;
  onFeedback?: (isHelpful: boolean, diagnosis: string) => void;
  onCardToggle?: (
    isOpen: boolean,
    diagnosis: string,
    signalStrength: string,
  ) => void;
  showLoadingPlaceholder?: boolean;
}

export const DifferentialDiagnosesOutput: React.FC<
  DifferentialDiagnosesOutputProps
> = ({
  content,
  showTitle = false,
  onFeedback,
  onCardToggle,
  userAddedDiagnoses,
  showLoadingPlaceholder = false,
}) => {
  const { t } = useTranslation();
  const diagnoses = differentialDiagnosesSchema.safeParse(content);

  if (!diagnoses.success) {
    return null;
  }

  if (diagnoses.data.length === 0) {
    return null;
  }

  const strongSignal = diagnoses.data.filter(
    (d) => d.signal_strength === 'strong',
  );
  const moderateSignal = diagnoses.data.filter(
    (d) => d.signal_strength === 'moderate',
  );
  const weakSignal = diagnoses.data.filter((d) => d.signal_strength === 'weak');

  return (
    <Stack gap={6}>
      {showTitle && (
        <OutputTitle title={t('appointment.insights.differentialDiagnoses')} />
      )}

      {(Boolean(userAddedDiagnoses?.length) || showLoadingPlaceholder) && (
        <Stack gap={3}>
          <Text size="xs" weight="semibold" color="tertiary">
            {t('appointmentTools.outputs.underConsideration')}
          </Text>

          {showLoadingPlaceholder && <DifferentialDiagnosisCardLoading />}

          {userAddedDiagnoses?.map((diagnosis, index) => (
            <DifferentialDiagnosisCard
              key={diagnosis.candidate_diagnosis}
              {...mapDiagnosisToCardProps(diagnosis)}
              onFeedback={onFeedback}
              onCardToggle={onCardToggle}
              isOpenByDefault={index === 0}
            />
          ))}
        </Stack>
      )}

      {strongSignal.length > 0 && (
        <Stack gap={3}>
          <Text size="xs" weight="semibold" color="tertiary">
            {t('appointmentTools.outputs.mostLikely')}
          </Text>
          {strongSignal.map((diagnosis, index) => (
            <DifferentialDiagnosisCard
              key={diagnosis.candidate_diagnosis}
              {...mapDiagnosisToCardProps(diagnosis)}
              onFeedback={onFeedback}
              onCardToggle={onCardToggle}
              isOpenByDefault={index === 0 && !userAddedDiagnoses?.length}
            />
          ))}
        </Stack>
      )}

      {moderateSignal.length > 0 && (
        <Stack gap={3}>
          <Text size="xs" weight="semibold" color="tertiary">
            {t('appointmentTools.outputs.moderateSignal')}
          </Text>
          {moderateSignal.map((diagnosis) => (
            <DifferentialDiagnosisCard
              key={diagnosis.candidate_diagnosis}
              {...mapDiagnosisToCardProps(diagnosis)}
              onFeedback={onFeedback}
              onCardToggle={onCardToggle}
            />
          ))}
        </Stack>
      )}

      {weakSignal.length > 0 && (
        <Stack gap={3}>
          <Text size="xs" weight="semibold" color="tertiary">
            {t('appointmentTools.outputs.weakSignal')}
          </Text>
          {weakSignal.map((diagnosis) => (
            <DifferentialDiagnosisCard
              key={diagnosis.candidate_diagnosis}
              {...mapDiagnosisToCardProps(diagnosis)}
              onFeedback={onFeedback}
              onCardToggle={onCardToggle}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
